// Login page entry point
import 'firebaseui/dist/firebaseui.css';
import './styles/login.css';
import { initSentry } from '../shared/sentry-config.ts';
import * as Sentry from '@sentry/react';
// Initialize GA4 analytics
import { trackPageView, trackEvent } from '../shared/services/analyticsService.ts';
import { auth, firebase } from '../shared/firebase-config.js';
import * as firebaseui from 'firebaseui';
import { redirectWithLanguage, getCurrentLanguage, addLanguageToPath } from '../shared/utils/urlUtils.ts';
import i18n from '../shared/i18n/config.ts';

// Initialize Sentry before FirebaseUI initialization
initSentry('web-login', 'login');

// Track page view
trackPageView('Login');

const lang = getCurrentLanguage();
// Set HTML lang attribute immediately for FirebaseUI
document.documentElement.lang = lang;
i18n.changeLanguage(lang).then(() => {
  // Update page text after i18n is ready
  updatePageText();
  
  // Update title
  const titleElement = document.querySelector('title[data-i18n]');
  if (titleElement) {
    titleElement.textContent = i18n.t('login.title');
  }
  
  // Initialize language selector
  initLanguageSelector();
  
  // Initialize back to home button
  initBackToHomeButton();
});

// Update all text on page with data-i18n attributes
function updatePageText() {
  // Update elements with data-i18n attribute
  document.querySelectorAll('[data-i18n]').forEach(element => {
    const key = element.getAttribute('data-i18n');
    const translation = i18n.t(key);
    if (translation !== key) {
      element.textContent = translation;
    }
  });
  
  // Update placeholder attributes
  document.querySelectorAll('[data-i18n-placeholder]').forEach(element => {
    const key = element.getAttribute('data-i18n-placeholder');
    const translation = i18n.t(key);
    if (translation !== key) {
      element.placeholder = translation;
    }
  });
  
  // Update title attributes
  document.querySelectorAll('[data-i18n-title]').forEach(element => {
    const key = element.getAttribute('data-i18n-title');
    const translation = i18n.t(key);
    if (translation !== key) {
      element.title = translation;
    }
  });
  
  // Update FirebaseUI text if it's already rendered
  updateFirebaseUIText();
}

// Show account linking error message
function showAccountLinkingMessage() {
  const container = document.getElementById('firebaseui-auth-container');
  if (!container) return;
  
  // Remove any existing error messages
  const existingError = container.querySelector('.account-linking-error');
  if (existingError) {
    existingError.remove();
  }
  
  const errorDiv = document.createElement('div');
  errorDiv.className = 'account-linking-error';
  errorDiv.style.cssText = 'background: #fef3c7; border: 1px solid #f59e0b; color: #92400e; padding: 12px; border-radius: 4px; margin: 10px 0;';
  errorDiv.textContent = i18n.t('login.accountExistsWithDifferentCredential', 'An account with this email already exists. Please sign in with your existing provider (Google, etc.), and Microsoft will be automatically linked to your account.');
  container.insertBefore(errorDiv, container.firstChild);
}

// Update FirebaseUI button text with translations
function updateFirebaseUIText() {
  const container = document.getElementById('firebaseui-auth-container');
  if (!container) return;
  
  // Find FirebaseUI buttons and update their text
  const emailButton = container.querySelector('[data-provider-id="password"]');
  const googleButton = container.querySelector('[data-provider-id="google.com"]');
  const microsoftButton = container.querySelector('[data-provider-id="microsoft.com"]');
  const githubButton = container.querySelector('[data-provider-id="github.com"]');
  
  if (emailButton) {
    const buttonText = emailButton.querySelector('.firebaseui-idp-text') || 
                       emailButton.querySelector('span') ||
                       emailButton;
    if (buttonText) {
      // Always update email button text regardless of current content
      buttonText.textContent = i18n.t('login.signInWithEmail', 'Sign in with email');
    }
  }
  
  if (googleButton) {
    const buttonText = googleButton.querySelector('.firebaseui-idp-text') || 
                       googleButton.querySelector('span') ||
                       googleButton;
    if (buttonText && buttonText.textContent) {
      // Check if it contains Google-related text
      if (buttonText.textContent.includes('Google')) {
        buttonText.textContent = i18n.t('login.signInWithGoogle', 'Sign in with Google');
      }
    }
  }
  
  if (microsoftButton) {
    const buttonText = microsoftButton.querySelector('.firebaseui-idp-text') || 
                       microsoftButton.querySelector('span') ||
                       microsoftButton;
    if (buttonText && buttonText.textContent) {
      // Check if it contains Microsoft-related text
      if (buttonText.textContent.includes('Microsoft')) {
        buttonText.textContent = i18n.t('login.signInWithMicrosoft', 'Sign in with Microsoft');
      }
    }
  }
  
  if (githubButton) {
    const buttonText = githubButton.querySelector('.firebaseui-idp-text') || 
                       githubButton.querySelector('span') ||
                       githubButton;
    if (buttonText && buttonText.textContent) {
      // Check if it contains GitHub-related text
      if (buttonText.textContent.includes('GitHub') || buttonText.textContent.includes('Github')) {
        buttonText.textContent = i18n.t('login.signInWithGitHub', 'Sign in with GitHub');
      }
    }
  }
}

// Language selector
const supportedLanguages = [
  { code: 'en', name: 'English' },
  { code: 'hu', name: 'Magyar' },
  { code: 'ar', name: 'العربية' },
  { code: 'fr', name: 'Français' },
  { code: 'zh-cn', name: '简体中文' },
  { code: 'ko', name: '한국어' },
  { code: 'it', name: 'Italiano' },
  { code: 'es', name: 'Español' },
  { code: 'ja', name: '日本語' },
  { code: 'ru', name: 'Русский' },
  { code: 'tr', name: 'Türkçe' },
  { code: 'pt-br', name: 'Português (Brasil)' },
];

function initBackToHomeButton() {
  const backToHomeBtn = document.getElementById('backToHomeBtn');
  if (!backToHomeBtn) return;
  
  backToHomeBtn.addEventListener('click', () => {
    redirectWithLanguage('/');
  });
}

function initLanguageSelector() {
  const languageToggle = document.getElementById('languageToggle');
  const languageText = document.getElementById('languageText');
  const languageDropdown = document.getElementById('languageDropdown');
  const languageSwitcher = languageToggle?.closest('.language-switcher');
  
  if (!languageToggle || !languageDropdown || !languageSwitcher) return;
  
  const currentLang = getCurrentLanguage();
  
  // Update current language display
  const currentLangInfo = supportedLanguages.find(lang => lang.code === currentLang);
  if (currentLangInfo && languageText) {
    languageText.textContent = currentLangInfo.name;
  }
  
  // Populate dropdown
  languageDropdown.innerHTML = '';
  supportedLanguages.forEach(lang => {
    const button = document.createElement('button');
    button.textContent = lang.name;
    button.dataset.lang = lang.code;
    if (lang.code === currentLang) {
      button.classList.add('active');
    }
    button.addEventListener('click', (e) => {
      e.stopPropagation();
      handleLanguageChange(lang.code);
    });
    languageDropdown.appendChild(button);
  });
  
  // Toggle dropdown
  languageToggle.addEventListener('click', (e) => {
    e.stopPropagation();
    languageSwitcher.classList.toggle('open');
  });
  
  // Close dropdown when clicking outside
  document.addEventListener('click', (e) => {
    if (!languageSwitcher.contains(e.target)) {
      languageSwitcher.classList.remove('open');
    }
  });
  
  // Close dropdown on escape key
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && languageSwitcher.classList.contains('open')) {
      languageSwitcher.classList.remove('open');
    }
  });
}

function handleLanguageChange(langCode) {
  // Close dropdown immediately
  const languageSwitcher = document.querySelector('.language-switcher');
  if (languageSwitcher) {
    languageSwitcher.classList.remove('open');
  }
  
  // Save to localStorage
  localStorage.setItem('gantty_language', langCode);
  
  // Change i18next language
  i18n.changeLanguage(langCode).then(() => {
    // Update HTML lang attribute
    document.documentElement.lang = langCode;
    
    // Update page text
    updatePageText();
    
    // Update FirebaseUI language
    const ui = window.firebaseUIInstance;
    if (ui && ui.setLanguage) {
      const firebaseUILang = getFirebaseUILanguage(langCode);
      ui.setLanguage(firebaseUILang);
    }
    
    // Update URL with new language
    const currentPath = window.location.pathname;
    const pathWithoutLang = currentPath.replace(/^\/(en|hu|ar|fr|zh-cn|ko|it|es|ja|ru|tr|pt-br)/, '') || '/login';
    const newPath = addLanguageToPath(langCode, pathWithoutLang);
    
    // Update URL without reload
    if (currentPath !== newPath) {
      window.history.pushState({}, '', newPath);
    }
    
    // Update language selector display
    const languageText = document.getElementById('languageText');
    const languageDropdown = document.getElementById('languageDropdown');
    const currentLangInfo = supportedLanguages.find(lang => lang.code === langCode);
    
    if (currentLangInfo && languageText) {
      languageText.textContent = currentLangInfo.name;
    }
    
    // Update active state in dropdown
    if (languageDropdown) {
      languageDropdown.querySelectorAll('button').forEach(button => {
        if (button.dataset.lang === langCode) {
          button.classList.add('active');
        } else {
          button.classList.remove('active');
        }
      });
    }
    
    // Re-translate FirebaseUI form
    translateFirebaseUIForm();
    
    // Update FirebaseUI button text (including email button)
    updateFirebaseUIText();
  });
}

// Translate FirebaseUI form elements
function translateFirebaseUIForm() {
  const container = document.getElementById('firebaseui-auth-container');
  if (!container) return;
  
  const currentLang = getCurrentLanguage();
  
  // Skip translation for English (default FirebaseUI language)
  if (currentLang === 'en') return;
  
  // Map FirebaseUI English text to i18n keys
  const translationMap = {
    'Email': 'firebaseui.email',
    'Password': 'firebaseui.password',
    'Sign in': 'firebaseui.signIn',
    'Sign up': 'firebaseui.signUp',
    'Next': 'firebaseui.next',
    'Back': 'firebaseui.back',
    'Cancel': 'firebaseui.cancel',
    'Continue': 'firebaseui.continue',
    'Send': 'firebaseui.send',
    'Resend': 'firebaseui.resend',
    'Verify': 'firebaseui.verify',
    'Enter your email': 'firebaseui.enterYourEmail',
    'Enter your password': 'firebaseui.enterYourPassword',
    'Forgot password?': 'firebaseui.forgotPassword',
    'Create account': 'firebaseui.createAccount',
    'Already have an account?': 'firebaseui.alreadyHaveAccount',
    'Don\'t have an account?': 'firebaseui.dontHaveAccount',
    'Sign in with email': 'firebaseui.signInWithEmail',
    'Sign in with Google': 'firebaseui.signInWithGoogle',
    'Sign in with Microsoft': 'firebaseui.signInWithMicrosoft',
    'Sign in with GitHub': 'firebaseui.signInWithGitHub',
    'Email address': 'firebaseui.emailAddress',
    'Display name': 'firebaseui.displayName',
    'Confirm password': 'firebaseui.confirmPassword',
  };
  
  // Build translations object from i18n
  const translations = {};
  for (const [englishText, i18nKey] of Object.entries(translationMap)) {
    const translated = i18n.t(i18nKey);
    // Only add if translation exists and is different from the key (i18n fallback)
    if (translated && translated !== i18nKey) {
      translations[englishText] = translated;
    }
  }
  
  // Find and translate all text nodes
  const walker = document.createTreeWalker(
    container,
    NodeFilter.SHOW_TEXT,
    null,
    false
  );
  
  let node;
  while (node = walker.nextNode()) {
    const text = node.textContent.trim();
    if (translations[text]) {
      node.textContent = translations[text];
    }
  }
  
  // Translate placeholders
  container.querySelectorAll('input[placeholder]').forEach(input => {
    const placeholder = input.getAttribute('placeholder');
    if (translations[placeholder]) {
      input.setAttribute('placeholder', translations[placeholder]);
    }
  });
  
  // Translate labels
  container.querySelectorAll('label').forEach(label => {
    const text = label.textContent.trim();
    if (translations[text]) {
      label.textContent = translations[text];
    }
  });
  
  // Translate buttons
  container.querySelectorAll('button').forEach(button => {
    const text = button.textContent.trim();
    if (translations[text]) {
      button.textContent = translations[text];
    }
  });
}

// Configure FirebaseUI
// Map our language codes to FirebaseUI language codes
function getFirebaseUILanguage(lang) {
  // FirebaseUI uses standard language codes
  const langMap = {
    'en': 'en',
    'hu': 'hu',
    'ar-qa': 'ar',
    'ar-ae': 'ar',
    'ar-sa': 'ar',
    'ar-kw': 'ar',
    'fr': 'fr',
    'zh-cn': 'zh-CN',
    'ko': 'ko',
    'it': 'it',
    'es': 'es',
    'es-mx': 'es',
    'ja': 'ja',
    'ru': 'ru',
    'tr': 'tr',
    'pt-br': 'pt-BR',
  };
  return langMap[lang] || 'en';
}

const uiConfig = {
  // Set FirebaseUI language
  signInOptions: [
    // Email/Password provider - require display name for new users
    firebase.auth.EmailAuthProvider.PROVIDER_ID,
    firebase.auth.GoogleAuthProvider.PROVIDER_ID,
    firebase.auth.GithubAuthProvider.PROVIDER_ID,
    // Microsoft OAuth provider
    'microsoft.com'
  ],
  callbacks: {
    signInSuccessWithAuthResult: async (authResult, redirectUrl) => {
      // Check if we have a pending credential to link (from account-exists error in redirect flow)
      // Per Firebase docs: https://firebase.google.com/docs/auth/web/account-linking
      const pendingCredStr = sessionStorage.getItem('firebase_pending_credential');
      if (pendingCredStr) {
        try {
          const pendingCred = firebase.auth.AuthCredential.fromJSON(JSON.parse(pendingCredStr));
          const user = authResult.user;
          
          // Link the pending credential to the current user
          await user.linkWithCredential(pendingCred);
          console.log('[Login] Successfully linked credential to existing account');
          
          // Clear the pending credential and message flag
          sessionStorage.removeItem('firebase_pending_credential');
          sessionStorage.removeItem('firebase_show_linking_message');
        } catch (linkError) {
          console.error('[Login] Error linking credential:', linkError);
          Sentry.captureException(linkError, {
            tags: { component: 'login', flow: 'account-linking' },
          });
        }
      }
      
      // User successfully signed in
      const user = authResult.user;
      const isNewUser = authResult.additionalUserInfo?.isNewUser || false;
      const providerId = user.providerData[0]?.providerId || 'unknown';
      const method = 
        providerId === 'google.com' ? 'google' :
        providerId === 'microsoft.com' ? 'microsoft' :
        providerId === 'github.com' ? 'github' :
        'email';
      
      // Track analytics events
      if (isNewUser) {
        // Track sign up event
        trackEvent('sign_up', {
          method,
          user_id: user.uid,
        });
      }
      // Track login event (for both new and existing users)
      trackEvent('login', {
        method,
        user_id: user.uid,
      });
      
      // Add breadcrumb for successful login
      Sentry.addBreadcrumb({
        category: 'auth',
        message: 'User logged in successfully',
        level: 'info',
        data: {
          isNewUser,
          providerId,
        },
      });
      
      // Set user context in Sentry
      Sentry.setUser({
        id: user.uid,
        email: user.email || undefined,
      });
      
      // If this is a new user and displayName was provided, ensure it's set
      if (isNewUser && user.displayName) {
        console.log('[Login] New user created with displayName:', user.displayName);
        // The displayName should already be set by FirebaseUI, but we can verify
        // The Cloud Function will pick it up from user.displayName
      } else if (isNewUser && !user.displayName) {
        console.warn('[Login] New user created but displayName is missing');
      }
      
      // Redirect to profile page with language prefix
      console.log('[Login] Redirecting to profile page');
      redirectWithLanguage('/profile');
      
      console.log('[Login] Sign in success with auth result:', authResult);
      return false;
    },
    signInFailure: (error) => {
      console.error('[Login] Sign in failure:', error);
      
      // Handle account-exists-with-different-credential error
      // Per Firebase docs: https://firebase.google.com/docs/auth/web/account-linking
      // For redirect mode, the pending credential must be cached between page redirects
      if (error.code === 'auth/account-exists-with-different-credential') {
        console.log('[Login] Account exists with different credential, storing pending credential');
        
        // Store the pending credential in sessionStorage for redirect flow
        if (error.credential) {
          try {
            sessionStorage.setItem('firebase_pending_credential', JSON.stringify(error.credential.toJSON()));
            console.log('[Login] Pending credential stored, user needs to sign in with existing provider');
            
            // Show user-friendly message (will be shown when UI is rendered)
            // Store a flag to show the message
            sessionStorage.setItem('firebase_show_linking_message', 'true');
          } catch (storageError) {
            console.error('[Login] Error storing pending credential:', storageError);
          }
        }
      } else {
        // For other errors, capture in Sentry
        Sentry.captureException(error, {
          tags: { component: 'login', flow: 'firebaseui' },
        });
      }
      
      return Promise.resolve();
    },
    uiShown: () => {
      console.log('FirebaseUI widget rendered');
      Sentry.addBreadcrumb({
        category: 'auth',
        message: 'FirebaseUI widget rendered',
        level: 'info',
      });
      
      // Check if we need to show account linking message
      const showLinkingMessage = sessionStorage.getItem('firebase_show_linking_message');
      const hasPendingCredential = sessionStorage.getItem('firebase_pending_credential');
      if (showLinkingMessage === 'true' || hasPendingCredential) {
        // Wait a bit for the container to be fully rendered
        setTimeout(() => {
          showAccountLinkingMessage();
        }, 200);
      }
      
      // Also check for FirebaseUI error messages that might indicate account-exists error
      setTimeout(() => {
        const container = document.getElementById('firebaseui-auth-container');
        if (container) {
          // Look for FirebaseUI error messages
          const errorMessages = container.querySelectorAll('.firebaseui-error-message, [role="alert"]');
          errorMessages.forEach((errorEl) => {
            const errorText = errorEl.textContent || '';
            if (errorText.includes('account-exists') || 
                errorText.includes('different sign-in credentials') ||
                errorText.includes('different credential')) {
              console.log('[Login] Detected account-exists error in FirebaseUI message');
              // Store flag to show our custom message
              sessionStorage.setItem('firebase_show_linking_message', 'true');
              showAccountLinkingMessage();
            }
          });
        }
      }, 500);
      
      // Update FirebaseUI button text with translations
      setTimeout(() => {
        updateFirebaseUIText();
        // Also try to translate form elements
        translateFirebaseUIForm();
      }, 100);
      
      // Set up observer to translate form when it appears
      const observer = new MutationObserver(() => {
        translateFirebaseUIForm();
      });
      
      const container = document.getElementById('firebaseui-auth-container');
      if (container) {
        observer.observe(container, { childList: true, subtree: true });
      }
    }
  },
  // Use redirect instead of popup to allow language translation
  // Popup mode opens in a separate window where our translations don't apply
  signInFlow: 'redirect',
  signInSuccessUrl: '/profile', // Set to prevent warning; callback handles actual redirect
  // Force the correct authDomain
  credentialHelper: firebaseui.auth.CredentialHelper.NONE
  // Terms of service url - removed to hide terms text
  // tosUrl: '/terms',
  // Privacy policy url - removed to hide privacy text
  // privacyPolicyUrl: '/privacy'
};

// Check for redirect result errors before initializing FirebaseUI
// This catches errors that occur during redirect flow
// Per Firebase docs: getRedirectResult() is used to get the result of a redirect-based sign-in
firebase.auth().getRedirectResult().then((result) => {
  // If there's no user but we have a credential, it might be an account-exists error
  // FirebaseUI handles this internally, but we can check for pending credentials
  if (!result.user && result.credential) {
    console.log('[Login] Redirect result has credential but no user - might be account-exists error');
  }
}).catch((error) => {
  console.error('[Login] Error getting redirect result:', error);
  
  // Handle account-exists-with-different-credential error
  if (error.code === 'auth/account-exists-with-different-credential') {
    console.log('[Login] Account exists with different credential, storing pending credential');
    
    // Store the pending credential in sessionStorage for redirect flow
    if (error.credential) {
      try {
        sessionStorage.setItem('firebase_pending_credential', JSON.stringify(error.credential.toJSON()));
        console.log('[Login] Pending credential stored, user needs to sign in with existing provider');
        
        // Store a flag to show the message
        sessionStorage.setItem('firebase_show_linking_message', 'true');
      } catch (storageError) {
        console.error('[Login] Error storing pending credential:', storageError);
      }
    }
  } else {
    // For other errors, capture in Sentry
    Sentry.captureException(error, {
      tags: { component: 'login', flow: 'redirect-result' },
    });
  }
});

// Also listen to auth state changes to catch errors that FirebaseUI might not surface
// This is a fallback for redirect flow errors
let authStateCheckDone = false;
firebase.auth().onAuthStateChanged((user) => {
  // Only check once on initial load
  if (!authStateCheckDone && !user) {
    authStateCheckDone = true;
    
    // Check URL for error parameters (Firebase might put errors in URL)
    const urlParams = new URLSearchParams(window.location.search);
    const errorCode = urlParams.get('error');
    const errorMessage = urlParams.get('error_description');
    
    if (errorCode || errorMessage) {
      console.log('[Login] Found error in URL params:', errorCode, errorMessage);
      
      // Check if this looks like an account-exists error
      if (errorMessage && errorMessage.includes('account-exists') || 
          errorCode === 'account-exists-with-different-credential') {
        // We can't get the credential from URL, but we can show the message
        // The credential should be stored by signInFailure if it was called
        sessionStorage.setItem('firebase_show_linking_message', 'true');
      }
    }
  }
});

// Initialize FirebaseUI
const ui = new firebaseui.auth.AuthUI(auth);
// Store globally for language selector access
window.firebaseUIInstance = ui;

ui.start('#firebaseui-auth-container', uiConfig);

// Check for pending credential on page load (in case of redirect flow)
// This ensures the message is shown even if the page is refreshed
setTimeout(() => {
  const hasPendingCredential = sessionStorage.getItem('firebase_pending_credential');
  const showLinkingMessage = sessionStorage.getItem('firebase_show_linking_message');
  if (hasPendingCredential || showLinkingMessage === 'true') {
    showAccountLinkingMessage();
  }
}, 500);
